import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Trash2, Plus, GripVertical, Loader2 } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";

import { toast } from "sonner";

interface MediaItem {
  id: string;
  url: string;
  type: string;
  parentId: string;
  isFeatured: boolean;
  order: number;
}

interface MediaManagerProps {
  type: "POST" | "PROJECT";
  parentId: string;
}

export function MediaManager({ type, parentId }: MediaManagerProps) {
  const queryClient = useQueryClient();
  const [newUrl, setNewUrl] = useState("");
  const [items, setItems] = useState<MediaItem[]>([]);

  const { data: mediaData, isLoading } = useQuery<MediaItem[]>({
    queryKey: ["media", type, parentId],
    queryFn: async () => {
      const res = await fetch(`/api/media/${type}/${parentId}`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
    enabled: !!parentId,
  });

  useEffect(() => {
    if (mediaData) {
      setItems(mediaData);
    }
  }, [mediaData]);

  const addMutation = useMutation({
    mutationFn: async (url: string) => {
      const res = await fetch("/api/media", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          type,
          parentId,
          order: items.length,
          isFeatured: items.length === 0, // First one featured by default
        }),
      });
      if (!res.ok) throw new Error("Failed to add media");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", type, parentId] });
      setNewUrl("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/media/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete media");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", type, parentId] });
    },
  });

  const featureMutation = useMutation({
    mutationFn: async (id: string) => {
      // 1. Update media table
      const res = await fetch(`/api/media/${id}/feature`, { method: "PUT" });
      if (!res.ok) throw new Error("Failed to set featured");
      
      // 2. Sync to parent coverImage for fast listing
      const item = items.find(i => i.id === id);
      if (item) {
        const parentUrl = type === "PROJECT" 
          ? `/api/projects/${parentId}` 
          : `/api/posts/${parentId}`;
          
        await fetch(parentUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ coverImage: item.url }),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", type, parentId] });
      queryClient.invalidateQueries({ queryKey: [type === "PROJECT" ? "/api/projects" : "/api/posts"] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (newItems: MediaItem[]) => {
      const updates = newItems.map((item, index) => ({ id: item.id, order: index }));
      const res = await fetch("/api/media/reorder", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to reorder media");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media", type, parentId] });
    },
  });

  const handleReorder = (newOrder: MediaItem[]) => {
    setItems(newOrder);
    reorderMutation.mutate(newOrder);
  };

  const handleAdd = () => {
    if (newUrl.trim()) {
      addMutation.mutate(newUrl);
    }
  };

  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { uploadToCloudinary } = await import("@/lib/uploadToCloudinary");
      const url = await uploadToCloudinary(file);
      addMutation.mutate(url);
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  if (!parentId) {
    return (
      <div className="p-4 border border-dashed rounded-lg text-center text-muted-foreground">
        Save the item first to manage media.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6">
        <div className="space-y-3 p-4 rounded-xl bg-accent/30 border border-border/50">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">Add New Media</Label>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 space-y-2">
              <Label className="text-[10px] text-muted-foreground ml-1">From URL</Label>
              <div className="flex gap-2">
                <Input 
                  value={newUrl} 
                  onChange={(e) => setNewUrl(e.target.value)} 
                  placeholder="https://images.unsplash.com/..." 
                  className="bg-background/50 border-border/60 focus-visible:ring-primary/30"
                />
                <Button 
                  onClick={handleAdd} 
                  disabled={addMutation.isPending || uploading || !newUrl.trim()}
                  className="bg-primary hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/20"
                >
                  {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">Add</span>
                </Button>
              </div>
            </div>

            <div className="shrink-0 space-y-2">
              <Label className="text-[10px] text-muted-foreground ml-1">From System</Label>
              <div className="relative">
                <input 
                  type="file" 
                  id="file-upload"
                  accept="image/*" 
                  onChange={handleFileUpload} 
                  disabled={uploading || addMutation.isPending}
                  className="hidden"
                />
                <label 
                  htmlFor="file-upload"
                  className={`
                    flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-border/60 
                    bg-background/50 hover:bg-accent/50 cursor-pointer transition-all duration-200
                    ${uploading ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <Plus className="h-4 w-4 text-primary" />
                  )}
                  <span className="text-sm font-medium">Choose File</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">Gallery (Drag to reorder)</Label>
          <span className="text-[10px] text-muted-foreground">{items.length} Images</span>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center p-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary/40" />
          </div>
        ) : (
          <Reorder.Group axis="y" values={items} onReorder={handleReorder} className="space-y-3">
            {items.map((item) => (
              <Reorder.Item key={item.id} value={item} className="list-none">
                <Card className="group relative overflow-hidden border-border/40 bg-accent/10 hover:bg-accent/20 transition-all duration-300 hover:border-primary/20 hover:shadow-md">
                  <CardContent className="p-3 flex items-center gap-4">
                    <div className="cursor-grab active:cursor-grabbing hover:text-primary transition-colors">
                      <GripVertical className="h-5 w-5 text-muted-foreground/50" />
                    </div>
                    
                    <div className="h-14 w-20 shrink-0 rounded-lg overflow-hidden border border-border/50 bg-muted shadow-inner relative group-hover:scale-105 transition-transform duration-300">
                      <img src={item.url} alt="" className="h-full w-full object-cover" />
                      {item.isFeatured && (
                        <div className="absolute top-0 right-0 p-1 bg-yellow-500 rounded-bl-lg shadow-sm">
                          <Star className="h-2 w-2 text-white fill-current" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-muted-foreground/40 font-mono truncate">
                        {item.url.split("/").pop()}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${item.isFeatured ? "bg-yellow-500/10 text-yellow-500" : "bg-muted text-muted-foreground"}`}>
                          {item.isFeatured ? "Featured Image" : "Gallery Image"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 hover:bg-yellow-500/10 ${item.isFeatured ? "text-yellow-500" : "text-muted-foreground"}`}
                        onClick={() => featureMutation.mutate(item.id)}
                        disabled={featureMutation.isPending}
                        title={item.isFeatured ? "Currently Featured" : "Set as Featured"}
                      >
                        <Star className={`h-4 w-4 ${item.isFeatured ? "fill-current" : ""}`} />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => deleteMutation.mutate(item.id)}
                        disabled={deleteMutation.isPending}
                        title="Delete Image"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}
        
        {items.length === 0 && !isLoading && (
          <div className="p-16 border-2 border-dashed border-border/40 rounded-2xl text-center flex flex-col items-center justify-center gap-3 bg-accent/5">
            <div className="p-4 rounded-full bg-accent/20">
              <Plus className="h-8 w-8 text-muted-foreground/30" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">No images in gallery</p>
              <p className="text-xs text-muted-foreground/60">Upload files or add URLs to get started.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
